import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { DEMO_PHOTOS } from '../lib/demoPhotos';

const ROLE_META = {
  candidate: { icon: '🎯', label: 'Candidate' },
  voucher: { icon: '🤝', label: 'Voucher' },
  hiring_manager: { icon: '💼', label: 'Hiring Manager' },
  none: { icon: '❓', label: 'No Role' },
};

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null); // email or 'all'
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    // Retry on AbortError — the Supabase client can abort the first request
    // when auth state is still initializing
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data, error } = await supabase.rpc('admin_get_demo_status');
        if (error) {
          if (error.message?.includes('aborted') && attempt < 2) {
            await new Promise(r => setTimeout(r, 200));
            continue;
          }
          setMessage({ type: 'error', text: `RPC error: ${error.message}` });
          setUsers([]);
        } else {
          const patched = (data || []).map(u => ({
            ...u,
            photo_url: DEMO_PHOTOS[u.email] || u.photo_url,
          }));
          setUsers(patched);
          setMessage(null);
        }
        break;
      } catch (err) {
        if (err.message?.includes('aborted') && attempt < 2) {
          await new Promise(r => setTimeout(r, 200));
          continue;
        }
        setMessage({ type: 'error', text: `Network error: ${err.message}` });
        setUsers([]);
        break;
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Refetch when user switches back to this PWA
  useVisibilityRefresh(fetchStatus);

  const handleResetUser = async (email) => {
    setResetting(email);
    setMessage(null);
    const { error } = await supabase.rpc('admin_reset_demo_user', { p_email: email });
    if (error && !error.message?.includes('aborted')) {
      setMessage({ type: 'error', text: `Reset failed: ${error.message}` });
    } else if (!error) {
      setMessage({ type: 'success', text: `${email} has been reset to fresh state.` });
    }
    setResetting(null);
    await fetchStatus();
  };

  const handleResetAll = async () => {
    setResetting('all');
    setMessage(null);
    const { error } = await supabase.rpc('admin_reset_all_demo');
    if (error && !error.message?.includes('aborted')) {
      setMessage({ type: 'error', text: `Full reset failed: ${error.message}` });
    } else if (!error) {
      setMessage({ type: 'success', text: 'All demo data has been reset and re-seeded.' });
    }
    setResetting(null);
    await fetchStatus();
  };

  const roleMeta = (role) => ROLE_META[role] || ROLE_META.none;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0B1F3B 0%, #122B4F 50%, #18B6B2 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: '2rem 1rem',
    }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <img
              src="/brand/vouch/assets/vector/vouch-wordmark-horizontal.svg"
              alt="VOUCH"
              height="32"
              style={{ display: 'block', filter: 'brightness(0) invert(1)' }}
            />
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
              Admin Dashboard
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <a
              href="/auth"
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.8125rem',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              Back to Auth
            </a>
            <a
              href="/demo"
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.8125rem',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              3-Panel Demo
            </a>
          </div>
        </div>

        {/* Message banner */}
        {message && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            background: message.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: message.type === 'success' ? '#6ee7b7' : '#fca5a5',
          }}>
            {message.text}
          </div>
        )}

        {/* Reset All button */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            onClick={handleResetAll}
            disabled={resetting !== null}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: resetting === 'all'
                ? '#94a3b8'
                : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              cursor: resetting !== null ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            }}
          >
            {resetting === 'all' ? 'Resetting all demo data...' : 'Reset All Demo Data & Re-Seed'}
          </button>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6875rem', textAlign: 'center', marginTop: '0.375rem' }}>
            Clears everything and restores profiles, positions, connections, and job assignments
          </p>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '1rem',
          }}>
            Loading demo user status...
          </div>
        )}

        {/* User cards */}
        {!loading && users.map((u) => {
          const meta = roleMeta(u.role);
          const isResettingThis = resetting === u.email;
          const isClean = !u.linkedin_connected && u.connections_count === 0;

          return (
            <div
              key={u.email}
              style={{
                background: 'white',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                marginBottom: '0.75rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                {u.photo_url ? (
                  <img
                    src={u.photo_url}
                    alt=""
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #18B6B2',
                    }}
                  />
                ) : (
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                  }}>
                    {meta.icon}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: '700', color: '#0B1F3B', fontSize: '1rem' }}>
                      {u.display_name}
                    </span>
                    <span style={{
                      fontSize: '0.6875rem',
                      background: '#0B1F3B',
                      color: 'white',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '1rem',
                      fontWeight: '500',
                    }}>
                      {meta.icon} {meta.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    {u.email}
                  </div>
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  padding: '0.25rem 0.625rem',
                  borderRadius: '1rem',
                  background: isClean ? '#f1f5f9' : '#ecfdf5',
                  color: isClean ? '#94a3b8' : '#059669',
                }}>
                  {isClean ? 'Clean' : 'Has Data'}
                </div>
              </div>

              {/* Stats grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '0.5rem',
                marginBottom: '0.75rem',
              }}>
                <StatBadge
                  label="LinkedIn"
                  value={u.linkedin_connected ? 'Connected' : 'Not Set'}
                  active={u.linkedin_connected}
                />
                <StatBadge label="Connections" value={u.connections_count} active={u.connections_count > 0} />
                <StatBadge label="Vouch Links" value={u.vouch_connections_count} active={u.vouch_connections_count > 0} />
                {u.role === 'candidate' && (
                  <StatBadge label="Available Jobs" value={u.network_jobs_count} active={u.network_jobs_count > 0} />
                )}
                {u.role === 'hiring_manager' && (
                  <StatBadge label="Jobs Offered" value={u.jobs_offered_count} active={u.jobs_offered_count > 0} />
                )}
                {u.role === 'voucher' && (
                  <StatBadge label="Eligible Jobs" value={u.eligible_jobs_count} active={u.eligible_jobs_count > 0} />
                )}
                <StatBadge label="Job Interests" value={u.job_interests_count} active={u.job_interests_count > 0} />
                <StatBadge label="Vouches" value={u.vouches_count} active={u.vouches_count > 0} />
                <StatBadge label="Messages" value={u.messages_count} active={u.messages_count > 0} />
              </div>

              {/* Reset button */}
              <button
                onClick={() => handleResetUser(u.email)}
                disabled={resetting !== null}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: isResettingThis ? '#94a3b8' : '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.375rem',
                  color: '#64748b',
                  fontSize: '0.8125rem',
                  fontWeight: '500',
                  cursor: resetting !== null ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {isResettingThis ? 'Resetting...' : 'Reset This User'}
              </button>
            </div>
          );
        })}

        {/* No users found */}
        {!loading && users.length === 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '0.75rem',
            padding: '2rem',
            textAlign: 'center',
          }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
              No demo users found.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '1rem' }}>
              Run these SQL statements in your Supabase SQL Editor:
            </p>
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              textAlign: 'left',
              fontSize: '0.6875rem',
              fontFamily: 'monospace',
              color: '#93c5fd',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
            }}>
{`-- 1. Run supabase/005_admin_functions.sql (all 3 functions + grants)

-- 2. Verify it works:
SELECT * FROM public.admin_get_demo_status();

-- 3. If empty, re-run the GRANT statements:
GRANT EXECUTE ON FUNCTION public.admin_get_demo_status() TO anon, authenticated;
GRANT EXECUTE ON FUNCTION public.admin_reset_demo_user(TEXT) TO anon, authenticated;
GRANT EXECUTE ON FUNCTION public.admin_reset_all_demo() TO anon, authenticated;`}
            </div>
            <button
              onClick={fetchStatus}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1.5rem',
                background: '#18B6B2',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({ label, value, active }) {
  return (
    <div style={{
      background: active ? '#f0fdfa' : '#f8fafc',
      border: `1px solid ${active ? '#99f6e4' : '#e2e8f0'}`,
      borderRadius: '0.375rem',
      padding: '0.375rem 0.5rem',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: '0.875rem',
        fontWeight: '700',
        color: active ? '#0d9488' : '#94a3b8',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '0.625rem',
        color: '#6B7280',
        fontWeight: '500',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
      }}>
        {label}
      </div>
    </div>
  );
}
